import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { FleetScheduler, SchedulerDriver, SchedulerTruck, SchedulerOrder, ServiceHold, DayOff } from "@/lib/fleet-scheduler";
import { OrderWithRelations } from "@/components/schedule/types";
import { TractorWithHome, DriverWithHome } from "@/types/resources";
import { WorkOrder, DriverDayOff } from "@/types/tms";
import { getStaleOrderIds, normalizeCity } from "@/lib/location-utils";
import {
  getTractorServiceStatus,
  isBlockingOosStatus,
  type ServiceRecordLike,
} from "@/lib/tractor-service-status";

interface AssignPlusInput {
  orders: OrderWithRelations[];
  tractors: TractorWithHome[];
  drivers: DriverWithHome[];
  workOrders?: WorkOrder[];
  daysOff?: DriverDayOff[];
}

interface AssignmentResult {
  orderId: string;
  tractorId: string;
  tractorName: string;
  driverId: string;
  driverName: string;
}

/** Map tractor capabilities to a priority number (lower = preferred). */
function getTruckPriority(capabilities: string[]): number {
  if (capabilities.includes("NVO")) return 1;
  if (capabilities.includes("VO")) return 2;
  if (capabilities.includes("Manual")) return 3;
  return 999;
}

export function useFleetSchedulerAssignment(weekStart: Date, turnTimeMinutes: number) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ orders, tractors, drivers, workOrders, daysOff }: AssignPlusInput) => {
      // OOS exclusion. Fetch active
      // tractor_service_records once and drop any tractor whose current
      // status is blocking (oos_current / oos_past_due / oos_indefinite) so
      // auto-assignment never suggests a truck that dispatch cannot use.
      const oosBlockedTractorIds = new Set<string>();
      const { data, error } = await supabase
        .from("tractor_service_records" as never)
        .select("tractor_id, status, window_start, window_end")
        .in("status", ["open", "updated"]);
      if (error) throw error;
      const rows = (data ?? []) as unknown as Array<
        ServiceRecordLike & { tractor_id: string | null }
      >;
      const byTractor = new Map<string, ServiceRecordLike[]>();
      for (const r of rows) {
        if (!r.tractor_id) continue;
        const arr = byTractor.get(r.tractor_id) ?? [];
        arr.push(r);
        byTractor.set(r.tractor_id, arr);
      }
      byTractor.forEach((recs, id) => {
        if (isBlockingOosStatus(getTractorServiceStatus(recs))) {
          oosBlockedTractorIds.add(id);
        }
      });
      tractors = tractors.filter((t) => !oosBlockedTractorIds.has(t.id));

      // 1. Filter eligible orders
      const eligible = orders.filter(
        (o) =>
          o.status !== "canceled" &&
          o.status !== "in_transit" &&
          o.status !== "delivered" &&
          !o.assigned_tractor_id &&
          !o.assigned_operator_id &&
          !o.is_work_order
      );

      if (eligible.length === 0) {
        return { applied: [] as AssignmentResult[], failed: [] as string[], skipped: 0 };
      }

      // 2. Collect prior-week order IDs we need to fetch for location seeding
      const weekOrderIds = new Set(orders.map((o) => o.id));
      const priorOrderIds = new Set<string>();

      for (const t of tractors) {
        const ids = t.order_ids || [];
        // Walk backwards to find the last order not in current week
        for (let i = ids.length - 1; i >= 0; i--) {
          if (!weekOrderIds.has(ids[i])) {
            priorOrderIds.add(ids[i]);
            break;
          }
        }
      }
      for (const d of drivers) {
        const ids = d.order_ids || [];
        for (let i = ids.length - 1; i >= 0; i--) {
          if (!weekOrderIds.has(ids[i])) {
            priorOrderIds.add(ids[i]);
            break;
          }
        }
      }

      // Fetch prior orders for location seeding
      const priorOrderMap = new Map<string, { ship_to_city: string; requested_delivery_time: string }>();
      if (priorOrderIds.size > 0) {
        const { data: priorOrders } = await supabase
          .from("orders")
          .select("id, ship_to_city, requested_delivery_time")
          .in("id", Array.from(priorOrderIds));
        if (priorOrders) {
          for (const po of priorOrders) {
            priorOrderMap.set(po.id, { ship_to_city: po.ship_to_city, requested_delivery_time: po.requested_delivery_time });
          }
        }
      }

      // Helper: derive location + availability from resource's order_ids chain
      const deriveState = (orderIds: string[] | null, homeCity: string | null): { location: string; availableTime: string | null } => {
        const ids = orderIds || [];
        for (let i = ids.length - 1; i >= 0; i--) {
          const weekOrder = orders.find((o) => o.id === ids[i]);
          if (weekOrder) {
            const arrivalIso = new Date(weekOrder.requested_delivery_time).toISOString();
            const availableMs = new Date(weekOrder.requested_delivery_time).getTime() + (turnTimeMinutes * 60 * 1000);
            return { location: normalizeCity(weekOrder.ship_to_city), availableTime: new Date(availableMs).toISOString() };
          }
          const prior = priorOrderMap.get(ids[i]);
          if (prior) {
            const availableMs = new Date(prior.requested_delivery_time).getTime() + (turnTimeMinutes * 60 * 1000);
            return { location: normalizeCity(prior.ship_to_city), availableTime: new Date(availableMs).toISOString() };
          }
        }
        return { location: normalizeCity(homeCity), availableTime: null };
      };

      // 3. Transform data — derive currentLocation + availableTime from order_ids chain
      const schedulerTrucks: SchedulerTruck[] = tractors.map((t) => {
        const state = deriveState(t.order_ids, t.home_base?.city || null);
        return {
          id: t.id,
          currentLocation: state.location,
          availableTime: state.availableTime,
          priority: getTruckPriority(t.capabilities || []),
        };
      });

      const schedulerDrivers: SchedulerDriver[] = drivers.map((d) => {
        const state = deriveState(d.order_ids, d.home_base?.city || null);
        return {
          id: d.id,
          homeBase: normalizeCity(d.home_base?.city || null),
          workDays: d.flight?.days ?? [1, 2, 3, 4, 5],
          shift: (d.shift as "AM" | "PM") || "AM",
          currentLocation: state.location,
          availableTime: state.availableTime,
          shiftStartTime: null,
          hoursDrivenToday: 0,
          currentTruckId: null,
        };
      });

      const schedulerOrders: SchedulerOrder[] = eligible.map((o) => ({
        id: o.id,
        startLocation: normalizeCity(o.ship_from_city),
        arrivalLocation: normalizeCity(o.ship_to_city),
        startTime: new Date(o.ready_time).toISOString(),
        arrivalTime: new Date(o.requested_delivery_time).toISOString(),
      }));

      // 3. Map constraint data
      const serviceHolds: ServiceHold[] = (workOrders || []).map((wo) => ({
        truckId: wo.tractor_id,
        startTime: new Date(wo.start_time).toISOString(),
        durationHours: Number(wo.duration_hours),
      }));

      const schedulerDaysOff: DayOff[] = (daysOff || []).map((ddo) => ({
        driverId: ddo.driver_id,
        date: ddo.date,
      }));

      // 4. Execute scheduler
      const scheduler = new FleetScheduler(
        schedulerTrucks,
        schedulerDrivers,
        turnTimeMinutes / 60,
        serviceHolds,
        schedulerDaysOff
      );
      const result = scheduler.scheduleOrders(schedulerOrders);

      if (result.scheduledOrders.length === 0) {
        return { applied: [] as AssignmentResult[], failed: [] as string[], skipped: eligible.length };
      }

      // 5. Build assignment results with names for logging
      const tractorMap = new Map(tractors.map((t) => [t.id, t]));
      const driverMap = new Map(drivers.map((d) => [d.id, d]));

      const assignments: AssignmentResult[] = result.scheduledOrders.map((so) => ({
        orderId: so.id,
        tractorId: so.assignedTruckId!,
        tractorName: tractorMap.get(so.assignedTruckId!)?.name || "Unknown",
        driverId: so.assignedDriverId!,
        driverName: driverMap.get(so.assignedDriverId!)?.name || "Unknown",
      }));

      // 6. Persist with validation + retry
      const staleIds = getStaleOrderIds(orders);

      // First pass: try all writes and check responses
      const writeResults = await Promise.all(
        assignments.map(async (a) => {
          const { error } = await supabase
            .from("orders")
            .update({ assigned_tractor_id: a.tractorId, assigned_operator_id: a.driverId })
            .eq("id", a.orderId);
          return { assignment: a, error };
        })
      );

      const applied: AssignmentResult[] = writeResults.filter((r) => !r.error).map((r) => r.assignment);
      let failed: AssignmentResult[] = writeResults.filter((r) => r.error).map((r) => r.assignment);

      // Retry pass: retry failed writes once
      if (failed.length > 0) {
        console.warn(`[Assignment+] ${failed.length} order update(s) failed, retrying...`, failed.map((a) => a.orderId));
        const retryResults = await Promise.all(
          failed.map(async (a) => {
            const { error } = await supabase
              .from("orders")
              .update({ assigned_tractor_id: a.tractorId, assigned_operator_id: a.driverId })
              .eq("id", a.orderId);
            return { assignment: a, error };
          })
        );
        applied.push(...retryResults.filter((r) => !r.error).map((r) => r.assignment));
        failed = retryResults.filter((r) => r.error).map((r) => r.assignment);
        if (failed.length > 0) {
          console.error(`[Assignment+] ${failed.length} order update(s) still failed after retry`, failed.map((a) => a.orderId));
        }
      }

      // Use only applied (confirmed) assignments for downstream operations
      if (applied.length > 0) {
        const byTractor = new Map<string, AssignmentResult[]>();
        const byDriver = new Map<string, AssignmentResult[]>();
        for (const a of applied) {
          byTractor.set(a.tractorId, [...(byTractor.get(a.tractorId) || []), a]);
          byDriver.set(a.driverId, [...(byDriver.get(a.driverId) || []), a]);
        }

        await Promise.all(
          Array.from(byTractor.entries()).map(([tractorId, aList]) => {
            const tractor = tractorMap.get(tractorId);
            if (!tractor) return Promise.resolve();
            const currentIds = tractor.order_ids || [];
            const newIds = aList.map((a) => a.orderId);
            const combined = [...new Set([...currentIds.filter((id) => !staleIds.has(id)), ...newIds])];
            const sorted = orders
              .filter((o) => combined.includes(o.id))
              .sort((a, b) => new Date(a.ready_time).getTime() - new Date(b.ready_time).getTime())
              .map((o) => o.id);
            return supabase.from("tractors").update({ order_ids: sorted }).eq("id", tractorId).then();
          })
        );

        await Promise.all(
          Array.from(byDriver.entries()).map(([driverId, aList]) => {
            const driver = driverMap.get(driverId);
            if (!driver) return Promise.resolve();
            const currentIds = driver.order_ids || [];
            const newIds = aList.map((a) => a.orderId);
            const combined = [...new Set([...currentIds.filter((id) => !staleIds.has(id)), ...newIds])];
            const sorted = orders
              .filter((o) => combined.includes(o.id))
              .sort((a, b) => new Date(a.ready_time).getTime() - new Date(b.ready_time).getTime())
              .map((o) => o.id);
            return supabase.from("drivers").update({ order_ids: sorted }).eq("id", driverId).then();
          })
        );

        const logEntries = applied.flatMap((a) => [
          {
            order_id: a.orderId,
            field_name: "assigned_tractor_id",
            old_value: null,
            new_value: a.tractorName,
            changed_by: "System (Assignment+)",
          },
          {
            order_id: a.orderId,
            field_name: "assigned_operator_id",
            old_value: null,
            new_value: a.driverName,
            changed_by: "System (Assignment+)",
          },
        ]);
        await supabase.from("order_change_log").insert(logEntries);
      }

      const schedulerSkipped = eligible.length - assignments.length;
      return { applied, failed: failed.map((a) => a.orderId), skipped: schedulerSkipped };
    },
    onSuccess: ({ applied, failed, skipped }) => {
      queryClient.invalidateQueries({ queryKey: ["schedule-orders"] });
      queryClient.invalidateQueries({ queryKey: ["driver-schedule-orders"] });
      queryClient.invalidateQueries({ queryKey: ["driver-schedule-all-orders"] });
      queryClient.invalidateQueries({ queryKey: ["tractors-with-home-base"] });
      queryClient.invalidateQueries({ queryKey: ["drivers-with-flights"] });

      if (applied.length > 0) {
        let msg = `Assigned ${applied.length} order(s)`;
        if (skipped > 0) msg += `. ${skipped} could not be matched.`;
        if (failed.length > 0) msg += ` ${failed.length} failed to save.`;
        toast.success(msg);
        if (failed.length > 0) {
          toast.warning(`${failed.length} assignment(s) failed to persist after retry`);
        }
      } else if (failed.length > 0) {
        toast.error(`All ${failed.length} assignment(s) failed to save`);
      } else if (skipped > 0) {
        toast.info(`Could not assign any of ${skipped} eligible order(s)`);
      } else {
        toast.info("No eligible orders to assign");
      }
    },
    onError: (error: Error) => {
      toast.error(`Assignment failed: ${error.message}`);
    },
  });

  const clearMutation = useMutation({
    mutationFn: async ({
      orders,
      tractors,
      drivers,
    }: {
      orders: OrderWithRelations[];
      tractors: TractorWithHome[];
      drivers: DriverWithHome[];
    }) => {
      const assignedOrders = orders.filter(
        (o) => (o.assigned_tractor_id || o.assigned_operator_id) && !o.is_work_order
      );

      if (assignedOrders.length === 0) {
        return { cleared: 0 };
      }

      const orderIds = assignedOrders.map((o) => o.id);

      const { error } = await supabase
        .from("orders")
        .update({ assigned_tractor_id: null, assigned_operator_id: null })
        .in("id", orderIds);
      if (error) throw error;

      const tractorUpdates: PromiseLike<any>[] = [];
      for (const tractor of tractors) {
        const currentIds = tractor.order_ids || [];
        const newIds = currentIds.filter((id) => !orderIds.includes(id));
        if (newIds.length !== currentIds.length) {
          tractorUpdates.push(
            supabase.from("tractors").update({ order_ids: newIds }).eq("id", tractor.id).then()
          );
        }
      }
      await Promise.all(tractorUpdates);

      const driverUpdates: PromiseLike<any>[] = [];
      for (const driver of drivers) {
        const currentIds = driver.order_ids || [];
        const newIds = currentIds.filter((id) => !orderIds.includes(id));
        if (newIds.length !== currentIds.length) {
          driverUpdates.push(
            supabase.from("drivers").update({ order_ids: newIds }).eq("id", driver.id).then()
          );
        }
      }
      await Promise.all(driverUpdates);

      const logEntries = assignedOrders.flatMap((o) => {
        const entries: any[] = [];
        if (o.assigned_tractor_id) {
          entries.push({
            order_id: o.id,
            field_name: "assigned_tractor_id",
            old_value: o.assigned_tractor?.name || "Unknown",
            new_value: null,
            changed_by: "System (Clear assignments)",
          });
        }
        if (o.assigned_operator_id) {
          entries.push({
            order_id: o.id,
            field_name: "assigned_operator_id",
            old_value: o.assigned_operator?.name || "Unknown",
            new_value: null,
            changed_by: "System (Clear assignments)",
          });
        }
        return entries;
      });
      await supabase.from("order_change_log").insert(logEntries);

      return { cleared: assignedOrders.length };
    },
    onSuccess: ({ cleared }) => {
      queryClient.invalidateQueries({ queryKey: ["schedule-orders"] });
      queryClient.invalidateQueries({ queryKey: ["driver-schedule-orders"] });
      queryClient.invalidateQueries({ queryKey: ["driver-schedule-all-orders"] });
      queryClient.invalidateQueries({ queryKey: ["tractors-with-home-base"] });
      queryClient.invalidateQueries({ queryKey: ["drivers-with-flights"] });

      if (cleared > 0) {
        toast.success(`Cleared assignments from ${cleared} order(s)`);
      } else {
        toast.info("No assignments to clear");
      }
    },
    onError: (error: Error) => {
      toast.error(`Failed to clear assignments: ${error.message}`);
    },
  });

  return {
    assignPlus: mutation.mutate,
    isAssigningPlus: mutation.isPending,
    clear: clearMutation.mutate,
    isClearing: clearMutation.isPending,
  };
}
